import { useRef, useCallback, useEffect } from 'react'
import styles from './Slider.module.scss'

// ── Helpers ───────────────────────────────────────────────────────────────────

function snapToStep(value, min, max, step) {
  const snapped = Math.round((value - min) / step) * step + min
  return Math.min(max, Math.max(min, snapped))
}

function toPercent(value, min, max) {
  if (max === min) return 0
  return ((value - min) / (max - min)) * 100
}

const DEFAULT_FORMAT = (v) => String(v)

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Single-thumb slider primitive — sibling of RangeSlider but with one value.
 *
 * Shares the visual language (thumb, rail, tooltip-on-drag, hover-secondary
 * accent) and supports drag (mouse + touch), rail-click with transition, and
 * keyboard navigation. Thumb shape respects `--bim-shape-control` so the
 * project's radius theme applies (rectangular / rounded / pill).
 *
 * The caller renders any label / hour-marker row separately around the
 * primitive — this component only draws the rail + track + thumb.
 *
 * @param {object} props
 * @param {number}  props.value
 * @param {[number, number]} props.defaultValue       — [min, max] bounds
 * @param {number} [props.step=1]
 * @param {(v: number) => void} props.onChange
 * @param {(v: number) => string} [props.formatValue] — used for aria-valuetext + drag tooltip
 * @param {boolean} [props.isActive=false]            — pins accent on (e.g. focused state)
 * @param {boolean} [props.disabled=false]
 * @param {string}  [props.id]                        — base for aria-label
 * @param {string}  [props.ariaLabel]                 — explicit aria-label override
 */
export function Slider({
  value,
  defaultValue,
  step = 1,
  onChange,
  formatValue = DEFAULT_FORMAT,
  isActive = false,
  disabled = false,
  id,
  ariaLabel,
}) {
  const [min, max] = defaultValue

  const rootRef = useRef(null)
  const railRef = useRef(null)

  // Up-to-date value snapshot — avoids stale closures inside document listeners.
  const valueRef = useRef(value)
  useEffect(() => {
    valueRef.current = value
  }, [value])

  // ── Pointer → value ──────────────────────────────────────────────────────
  const valueFromClientX = useCallback(
    (clientX) => {
      const rail = railRef.current
      if (!rail) return null
      const rect = rail.getBoundingClientRect()
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
      return snapToStep(min + ratio * (max - min), min, max, step)
    },
    [min, max, step],
  )

  // ── Dragging state (mutate DOM directly to avoid re-render cost) ─────────
  const setDragging = useCallback((dragging) => {
    if (rootRef.current) {
      rootRef.current.dataset.dragging = dragging ? 'true' : 'false'
    }
    if (dragging) document.body.style.cursor = 'grabbing'
    else          document.body.style.cursor = ''
  }, [])

  // ── Drag (mouse + touch) ─────────────────────────────────────────────────
  const startDrag = useCallback(
    (e) => {
      if (disabled) return
      e.preventDefault()
      e.stopPropagation()
      setDragging(true)

      const onMove = (moveE) => {
        moveE.preventDefault()
        const clientX = moveE.touches ? moveE.touches[0].clientX : moveE.clientX
        const newVal = valueFromClientX(clientX)
        if (newVal === null) return
        if (newVal !== valueRef.current) onChange(newVal)
      }

      const onUp = () => {
        setDragging(false)
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
        document.removeEventListener('touchmove', onMove)
        document.removeEventListener('touchend', onUp)
      }

      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
      document.addEventListener('touchmove', onMove, { passive: false })
      document.addEventListener('touchend', onUp)
    },
    [disabled, valueFromClientX, onChange, setDragging],
  )

  // ── Rail click — transition enabled (data-dragging stays false) ──────────
  const handleRailPointerDown = useCallback(
    (e) => {
      if (disabled) return
      if (e.target.dataset.thumb) return
      const clientX = e.touches ? e.touches[0].clientX : e.clientX
      const newVal = valueFromClientX(clientX)
      if (newVal === null) return
      onChange(newVal)
    },
    [disabled, valueFromClientX, onChange],
  )

  // ── Keyboard ─────────────────────────────────────────────────────────────
  const handleKeyDown = useCallback(
    (e) => {
      if (disabled) return
      const range = max - min
      const bigStep = Math.max(step, Math.round((range * 0.1) / step) * step)
      let next = value

      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowDown':  next = value - step;    break
        case 'ArrowRight':
        case 'ArrowUp':    next = value + step;    break
        case 'Home':       next = min;             break
        case 'End':        next = max;             break
        case 'PageDown':   next = value - bigStep; break
        case 'PageUp':     next = value + bigStep; break
        default: return
      }

      e.preventDefault()
      next = Math.min(max, Math.max(min, snapToStep(next, min, max, step)))
      if (next !== value) onChange(next)
    },
    [disabled, value, min, max, step, onChange],
  )

  const percent = toPercent(value, min, max)

  return (
    <div
      ref={rootRef}
      className={styles.root}
      data-dragging="false"
      data-active={isActive || undefined}
      data-disabled={disabled || undefined}
      onMouseDown={handleRailPointerDown}
      onTouchStart={handleRailPointerDown}
    >
      <div ref={railRef} className={styles.rail}>
        <div className={styles.track} style={{ width: `${percent}%` }} />

        <div
          className={styles.thumb}
          data-thumb="single"
          role="slider"
          tabIndex={disabled ? -1 : 0}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
          aria-valuetext={formatValue(value)}
          aria-label={ariaLabel ?? id ?? 'slider'}
          style={{ left: `${percent}%` }}
          onMouseDown={startDrag}
          onTouchStart={startDrag}
          onKeyDown={handleKeyDown}
        >
          <span className={styles.tooltip} aria-hidden="true">{formatValue(value)}</span>
        </div>
      </div>
    </div>
  )
}
